import React, { useEffect, useMemo, useState } from 'react';
import { LuSearch, LuX, LuPlus, LuChevronRight } from 'react-icons/lu';
import api from '../../services/api';
import { coincideTodo } from '../../utils/busqueda';
import { esAbierto, cuandoPedido } from '../../utils/pedidosEmpresa';
import { diaLargo } from './diasRuta';
import ReservarDiaSheet from './ReservarDiaSheet';
import { Segmentado } from '../ui/Pantalla';
import MapaLeaflet from '../mapa/MapaLeaflet';
import FichaEquipoSheet from './FichaEquipoSheet';
import FichaClienteEmpresa, { SEM } from './FichaClienteEmpresa';
import FormClienteEmpresa from './FormClienteEmpresa';
import EquiposDeBaja from './EquiposDeBaja';
import ClientesDeBaja from './ClientesDeBaja';
import DescargarTodo from './DescargarTodo';
import MapaDiaEmpresa from './MapaDiaEmpresa';

// Portal Empresa → Clientes (13-oct-2026): sus clientes (lugares) en una lista compacta, igual que la
// de Clientes del admin pero sin nada interno (precios, deudas, teléfonos). Semáforo del service con el
// mismo criterio que "Services", Lista/Mapa, ficha con equipos y pedidos, alta y edición sin hacer un pedido.
const CHIP = (on) => `h-8 md:h-9 px-3 rounded-full shrink-0 inline-flex items-center gap-1.5 text-label font-bold border transition-all active:scale-95 ${on ? 'border-brand-red text-ink bg-[rgba(232,66,47,0.10)]' : 'border-black/10 dark:border-white/10 text-secondary'}`;

// Las otras direcciones del mismo cliente: mismo nombre y mismo código (18-oct-2026; 22-oct-2026 el
// nombre también cuenta, así dos clientes que quedaron con el mismo código no se mezclan)
const plano = (x) => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
const direccionesDe = (todos, cli) => todos.filter(c => c.id !== cli.id && plano(c.nombre) === plano(cli.nombre)
    && plano(c.codigo) === plano(cli.codigo));

// En el mapa, "Con pedido en curso" (25-oct-2026): cada punto con el color de cómo está su pedido, para
// decidir de un vistazo qué falta agendar y qué queda cerca de qué. El peor manda: sin día > reservado > con día.
const SITUACION = {
    // Mismos colores que el mapa del admin (26-oct-2026): naranja sin día, celeste reservado, rojo oscuro urgente
    SIN: { label: 'Sin día', color: '#EA580C', peso: 3 },
    RESERVA: { label: 'Reservado', color: '#0EA5E9', peso: 2 },
    DIA: { label: 'Con día', color: '#16A34A', peso: 1 },
};
const situacionDe = (p) => (p.fecha ? 'DIA' : p.rutaFecha ? 'RESERVA' : 'SIN');
const textoSituacion = (p) => (p.fecha ? cuandoPedido(p) : p.rutaFecha ? `Reservado para el ${diaLargo(p.rutaFecha)}` : 'Sin día');

// ── Lista ───────────────────────────────────────────────────────────────────
// "Hoy", "Mañana" o "Vie 30" para los botones de día del mapa
const DIAS_CORTO = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
function diaChip(iso) {
    const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    const f = new Date(a, m - 1, d), hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const dif = Math.round((f - hoy) / 86400000);
    return dif === 0 ? 'Hoy' : dif === 1 ? 'Mañana' : `${DIAS_CORTO[f.getDay()]} ${d}`;
}

export default function ClientesEmpresa({ pedidos = [], conMapa = true, soloMiLugar = false, empresa, onPedir, onAbrirPedido, onPedirEquipo, onRecargar }) {
    const [lista, setLista] = useState(null);
    const [q, setQ] = useState('');
    const [chip, setChip] = useState('todos');
    const [orden, setOrden] = useState('recientes');
    const [modo, setModo] = useState('lista');
    const [ficha, setFicha] = useState(null);
    const [form, setForm] = useState(null);       // null | {} | cliente
    const [equipo, setEquipo] = useState(null);
    const [selMapa, setSelMapa] = useState(null);
    const [eligiendo, setEligiendo] = useState(null);
    // Mapa por día (29-oct-2026): las visitas agendadas de cada día, en el orden del técnico
    const [agenda, setAgenda] = useState(null);
    useEffect(() => {
        if (modo !== 'mapa' || !conMapa || agenda) return;
        api.get('/empresa/agenda', { params: { dias: 7 } }).then(r => setAgenda(r.data)).catch(() => setAgenda({ dias: [] }));
    }, [modo, conMapa, agenda]);
    const diaSel = chip.startsWith('dia:') ? (agenda?.dias || []).find(d => `dia:${d.fecha}` === chip) : null;

    const [bajasKey, setBajasKey] = useState(0);
    const cargar = () => api.get('/empresa/clientes').then(r => setLista(Array.isArray(r.data) ? r.data : [])).catch(() => setLista(l => l || []));
    useEffect(() => { cargar(); }, []);

    // Pedido en curso y último pedido de cada cliente (de los pedidos que ya tiene el portal)
    const porSede = useMemo(() => {
        const m = {};
        pedidos.filter(p => !p.archivado).forEach(p => {
            const a = m[p.sedeId] || (m[p.sedeId] = { enCurso: 0, ultimo: '', abiertos: [], peor: null, urgente: false });
            if (esAbierto(p)) {
                a.enCurso++;
                a.abiertos.push(p);
                const sit = situacionDe(p);
                if (!a.peor || SITUACION[sit].peso > SITUACION[a.peor].peso) a.peor = sit;
                if (p.urgente) a.urgente = true;
            }
            if (String(p.creadoEn) > a.ultimo) a.ultimo = String(p.creadoEn);
        });
        return m;
    }, [pedidos]);

    const todos = lista || [];
    const CHIPS = [
        { id: 'todos', label: 'Todos', pasa: () => true },
        { id: 'vencido', label: 'Service vencido', pasa: c => c.estado === 'VENCIDO', alerta: true },
        { id: 'curso', label: 'Con pedido en curso', pasa: c => porSede[c.id]?.enCurso > 0 },
    ];
    const enBusqueda = todos.filter(c => coincideTodo(`${c.codigo || ''} ${c.nombre} ${c.direccion || ''} ${c.equipos.map(e => e.serie).join(' ')}`, q));
    const filtrados = enBusqueda.filter((CHIPS.find(x => x.id === chip) || CHIPS[0]).pasa).sort((a, b) => (orden === 'az'
        ? String(a.nombre).localeCompare(String(b.nombre), 'es')
        : (porSede[b.id]?.ultimo || '').localeCompare(porSede[a.id]?.ultimo || '')
            || String(b.ultimaVisita || '').localeCompare(String(a.ultimaVisita || ''))
            || String(a.nombre).localeCompare(String(b.nombre), 'es')));
    const verPedidos = chip === 'curso';
    const puntos = filtrados.filter(c => c.lat != null).map(c => {
        const ps = porSede[c.id];
        if (verPedidos && ps?.peor) return { id: c.id, lat: c.lat, lng: c.lng, color: ps.urgente && ps.peor !== 'DIA' ? '#991B1B' : SITUACION[ps.peor].color, etiqueta: ps.urgente ? '!' : null };
        return { id: c.id, lat: c.lat, lng: c.lng, color: (SEM[c.estado] || SEM.SIN_DATOS).color, etiqueta: c.equipos.length > 1 ? String(c.equipos.length) : null };
    });
    const enMapa = todos.find(c => c.id === selMapa);
    const pedidosMapa = verPedidos && enMapa ? (porSede[enMapa.id]?.abiertos || []) : [];
    const conteo = verPedidos ? filtrados.reduce((m, c) => { const k = porSede[c.id]?.peor; if (k) m[k] = (m[k] || 0) + 1; return m; }, {}) : null;
    const elegirDia = (p, c) => setEligiendo({ pedidoId: p.id, nombre: c.nombre, direccion: c.direccion, motivo: p.motivo, rutaFecha: p.rutaFecha, urgente: p.urgente });

    const pedirPara = (c) => { setFicha(null); onPedir && onPedir(c.id); };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                {conMapa ? <Segmentado valor={modo} onChange={(m) => { setModo(m); if (m === 'lista' && chip.startsWith('dia:')) setChip('todos'); }} opciones={[{ id: 'lista', label: 'Lista' }, { id: 'mapa', label: 'Mapa' }]} /> : <span />}
                <div className="flex items-center gap-2">
                    <DescargarTodo empresa={empresa} compacto />
                    {!soloMiLugar && (
                        <button type="button" onClick={() => setForm({})} className="h-10 px-3.5 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center gap-1.5 whitespace-nowrap active:scale-95">
                            <LuPlus size={15} /> Cliente nuevo
                        </button>
                    )}
                </div>
            </div>

            <label className="flex items-center gap-2 h-12 px-3.5 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06]">
                <LuSearch size={17} className="text-muted shrink-0" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar cliente, dirección o N/S"
                    className="flex-1 min-w-0 bg-transparent text-body text-ink outline-none placeholder:text-muted" />
                {q && <button type="button" onClick={() => setQ('')} aria-label="Borrar"><LuX size={16} className="text-muted" /></button>}
            </label>

            <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 sin-barra">
                {CHIPS.map(x => {
                    const n = x.id === 'todos' ? enBusqueda.length : enBusqueda.filter(x.pasa).length;
                    if (x.id !== 'todos' && !n) return null;
                    return (
                        <button key={x.id} type="button" onClick={() => setChip(x.id)} className={CHIP(chip === x.id)}>
                            {x.alerta && <span className="w-2 h-2 rounded-full bg-[#DC2626]" />}{x.label}<span className="text-muted">{n}</span>
                        </button>
                    );
                })}
                {modo === 'mapa' && (agenda?.dias || []).map(d => (
                    <button key={d.fecha} type="button" onClick={() => setChip(`dia:${d.fecha}`)} className={CHIP(chip === `dia:${d.fecha}`)}>
                        {diaChip(d.fecha)}<span className="text-muted">{d.visitas.length}</span>
                    </button>
                ))}
                <span className="flex-1" />
                {modo === 'lista' && <Segmentado valor={orden} onChange={setOrden} opciones={[{ id: 'recientes', label: 'Recientes' }, { id: 'az', label: 'A-Z' }]} />}
            </div>

            {lista === null ? (
                <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-card animate-pulse" />)}</div>
            ) : modo === 'mapa' && conMapa && diaSel ? (
                <MapaDiaEmpresa dia={diaSel} salida={agenda?.salida} onAbrirPedido={onAbrirPedido} />
            ) : modo === 'mapa' && conMapa ? (
                <div className="relative isolate rounded-2xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06]">
                    <MapaLeaflet puntos={puntos} seleccionado={selMapa} ajustarKey={`${puntos.length}-${chip}`}
                        onClickPunto={p => setSelMapa(p.id)} onClickMapa={() => setSelMapa(null)}
                        className="w-full h-[calc(var(--vh,1vh)*100-360px)] min-h-[340px]" />
                    {conteo && (
                        <div className="absolute top-3 left-14 right-3 z-[500] flex flex-wrap gap-1.5 pointer-events-none">
                            {Object.entries(SITUACION).filter(([k]) => conteo[k]).map(([k, v]) => (
                                <span key={k} className="h-8 px-3 rounded-full bg-card shadow text-caption font-bold text-ink inline-flex items-center gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />{v.label} {conteo[k]}
                                </span>
                            ))}
                        </div>
                    )}
                    {enMapa && !verPedidos && (
                        <div className="absolute left-3 right-3 bottom-3 z-[600] p-3.5 rounded-2xl bg-card shadow-xl flex items-center gap-3">
                            <span className="flex-1 min-w-0">
                                <span className="block text-body font-black text-ink truncate">{enMapa.nombre}</span>
                                <span className="block text-caption text-muted truncate">{enMapa.direccion}</span>
                            </span>
                            <button type="button" onClick={() => { setFicha(enMapa); setSelMapa(null); }} className="h-10 px-3 rounded-xl bg-chip text-label font-black text-ink">Ver</button>
                            <button type="button" onClick={() => pedirPara(enMapa)} className="h-10 px-3 rounded-xl bg-brand-red text-white text-label font-black">Pedir service</button>
                        </div>
                    )}
                    {enMapa && verPedidos && (
                        <div className="absolute left-3 right-3 bottom-3 z-[600] p-3.5 rounded-2xl bg-card shadow-xl space-y-2.5">
                            <button type="button" onClick={() => { setFicha(enMapa); setSelMapa(null); }} className="w-full flex items-center gap-2 text-left">
                                <span className="flex-1 min-w-0">
                                    <span className="block text-body font-black text-ink truncate">{enMapa.nombre}</span>
                                    <span className="block text-caption text-muted truncate">{enMapa.direccion}</span>
                                </span>
                                <LuChevronRight size={16} className="shrink-0 text-muted" />
                            </button>
                            {pedidosMapa.slice(0, 3).map(p => {
                                const sit = situacionDe(p);
                                return (
                                    <div key={p.id} className="flex items-center gap-2.5 pt-2.5 border-t border-black/[0.06] dark:border-white/[0.06]">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SITUACION[sit].color }} />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-caption font-black text-ink truncate">{p.urgente ? 'Urgente · ' : ''}{p.motivo || 'Service'}</span>
                                            <span className="block text-label text-muted truncate">{textoSituacion(p)}</span>
                                        </span>
                                        {sit === 'DIA'
                                            ? <button type="button" onClick={() => { setSelMapa(null); onAbrirPedido && onAbrirPedido(p); }} className="h-9 px-3 shrink-0 rounded-xl bg-chip text-label font-black text-ink">Ver pedido</button>
                                            : <button type="button" onClick={() => elegirDia(p, enMapa)} className={`h-9 px-3 shrink-0 rounded-xl text-label font-black ${sit === 'SIN' ? 'bg-brand-red text-white' : 'bg-chip text-ink'}`}>{sit === 'SIN' ? 'Elegir día' : 'Cambiar día'}</button>}
                                    </div>
                                );
                            })}
                            {pedidosMapa.length > 3 && <p className="text-label text-muted">Y {pedidosMapa.length - 3} más: tocá el nombre para verlos</p>}
                        </div>
                    )}
                    {puntos.length < filtrados.length && (
                        <span className="absolute top-3 right-3 z-[500] h-8 px-3 rounded-full bg-card shadow text-caption font-bold text-secondary inline-flex items-center">
                            {filtrados.length - puntos.length} sin ubicar
                        </span>
                    )}
                </div>
            ) : filtrados.length === 0 ? (
                <p className="py-10 text-center text-body text-muted rounded-2xl bg-card">
                    {todos.length === 0 ? 'Todavía no tenés clientes cargados.' : 'Ninguno coincide.'}
                </p>
            ) : (
                <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] divide-y divide-black/[0.06] dark:divide-white/[0.06] overflow-hidden">
                    {filtrados.map(c => {
                        const sem = SEM[c.estado] || SEM.SIN_DATOS;
                        const curso = porSede[c.id]?.enCurso || 0;
                        return (
                            <button key={c.id} type="button" onClick={() => setFicha(c)} className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-chip hover:bg-panel">
                                <span className="flex-1 min-w-0">
                                    <span className="block text-body font-black text-ink truncate">{c.codigo ? `${c.codigo} - ` : ''}{c.nombre}</span>
                                    <span className="block text-caption text-muted truncate">{c.direccion || 'Sin dirección'}</span>
                                </span>
                                {curso > 0 && <span className="shrink-0 h-6 px-2 rounded-full bg-[rgba(37,99,235,0.12)] text-[#2563EB] dark:text-[#60A5FA] text-label font-black">En curso</span>}
                                <span className="shrink-0 inline-flex items-center gap-1.5 text-caption text-secondary" title={sem.label}>
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: sem.color }} />{c.equipos.length} eq.
                                </span>
                                <LuChevronRight size={16} className="shrink-0 text-muted" />
                            </button>
                        );
                    })}
                </div>
            )}

            {lista !== null && modo === 'lista' && !soloMiLugar && <ClientesDeBaja key={`c${bajasKey}`} onCambio={cargar} />}
            {lista !== null && modo === 'lista' && <EquiposDeBaja key={bajasKey} onCambio={() => { cargar(); }} />}

            {ficha && (
                <FichaClienteEmpresa cli={todos.find(c => c.id === ficha.id) || ficha} pedidos={pedidos} puedeEditar={!soloMiLugar} onCambio={cargar}
                    onCerrar={() => setFicha(null)} onEditar={() => setForm(ficha)} onPedir={() => pedirPara(ficha)}
                    onAbrirPedido={(p) => { setFicha(null); onAbrirPedido && onAbrirPedido(p); }}
                    onEquipo={setEquipo}
                    hermanas={direccionesDe(todos, todos.find(c => c.id === ficha.id) || ficha)}
                    onAbrirOtra={(c) => setFicha(c)}
                    onOtraDireccion={() => setForm({ otraDe: todos.find(c => c.id === ficha.id) || ficha })}
                    onBaja={() => { setFicha(null); cargar(); setBajasKey(k => k + 1); }} />
            )}
            {eligiendo && <ReservarDiaSheet pedido={eligiendo} onCerrar={() => setEligiendo(null)}
                onListo={() => { setEligiendo(null); setSelMapa(null); onRecargar && onRecargar(); }} />}
            {form && <FormClienteEmpresa inicial={form.id || form.otraDe ? form : null} onCerrar={() => setForm(null)}
                onGuardado={() => { setForm(null); setFicha(null); cargar(); }} />}
            {equipo && <FichaEquipoSheet serie={equipo.serie} id={equipo.id} onCerrar={() => setEquipo(null)} onCambio={() => { cargar(); setBajasKey(k => k + 1); }}
                onPedirServicio={(eq) => { setEquipo(null); setFicha(null); onPedirEquipo && onPedirEquipo(eq); }} />}
        </div>
    );
}
